import { version } from '../package.json'

const isBrowser = typeof window !== 'undefined'

function attributeIsTrue(el: HTMLElement, name: string) {
  const value = el.getAttribute(name)
  return el.hasAttribute(name) && (value === 'true' || value === '')
}

// TODO: use blob-util
function dataUrlToBlob(dataurl: string) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)![1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new Blob([u8arr], { type: mime })
}

export const delay = <ResolveType = void>(ms = 0, value?: ResolveType, reject = false) =>
  new Promise<ResolveType>((resolve, _reject) =>
    setTimeout(() => (reject ? _reject(value) : resolve(value)), ms)
  )

if (isBrowser) {
  // Use this for wordpress + shopify
  class BuilderEditor extends HTMLElement {
    iframeRef: HTMLIFrameElement | null = null

    apiKey?: string

    VERSION = version

    ready = false

    onReadyHandles: Function[] = []

    get loaded() {
      return this.ready
    }

    // TODO: maybe do eval parse
    private tryJsonParse(str: string) {
      try {
        return JSON.parse(str)
      } catch (error) {
        return
      }
    }

    async waitUntilReady() {
      return new Promise(resolve => {
        if (this.ready) {
          resolve()
        } else {
          this.onReadyHandles.push(resolve)
        }
      })
    }

    connectedCallback() {
      addEventListener('message', this.onWindowMessage)

      const apiKey = this.getAttribute('api-key') || this.getAttribute('public-key')

      const iframe = document.createElement('iframe')
      const mobileOnly = attributeIsTrue(this, 'mobile-only')

      const dev = this.getAttribute('env') === 'dev'
      const beta = this.getAttribute('env') === 'beta'
      const qa = this.getAttribute('env') === 'qa'
      const overridePreviewUrl = this.getAttribute('url')

      const cachebust = attributeIsTrue(this, 'cachebust')

      // TODO: run certain of these things in attributeChangedCallback
      const data = this.getAttribute('data')
      const theme = this.getAttribute('theme')
      const options = this.getAttribute('options')
      const canvasMode = attributeIsTrue(this, 'canvas-mode')

      if (data) {
        this._json = this.tryJsonParse(data)
      }
      if (theme) {
        this._theme = this.tryJsonParse(theme)
      }

      if (options) {
        this._options = this.tryJsonParse(options)
      }

      if (apiKey) {
        this.apiKey = apiKey
      }

      const host = this.getAttribute('host')
        ? this.getAttribute('host')
        : dev
          ? 'http://localhost:1234'
          : qa
            ? 'https://qa.builder.io'
            : beta
              ? 'https://beta.builder.io'
              : 'https://builder.io'

      let url = host + '/embedded' + (apiKey ? '/' + apiKey : '') + '?'

      if (mobileOnly) {
        url += '&mobileOnly=true'
      }

      if (canvasMode) {
        url += '&canvasMode=true'
      }

      if (apiKey) {
        url += '&apiKey=' + encodeURIComponent(apiKey)
      }

      if (overridePreviewUrl) {
        url += '&overridePreviewUrl=' + encodeURIComponent(overridePreviewUrl)
      }

      if (cachebust) {
        const now = Date.now()
        url = url.replace('/studio?', '/studio/embedded/' + now + '?')
        url += '&cachebuster=' + now
        iframe.name = 'builder-' + now
      }

      iframe.src = url
      this.appendChild(iframe)
      this.iframeRef = iframe
      this.injectStyles()
    }

    private onWindowMessage = (event: MessageEvent) => {
      const { data } = event
      if (data) {
        if (data.type === 'builder.editorDataUpdated') {
          if (data.data && data.data.data && data.data.data.blocksString) {
            data.data.data.blocks = JSON.parse(data.data.data.blocksString)
            delete data.data.data.blocksString
          }
          this.dispatchEvent(new CustomEvent('change', { detail: data.data }))
        }
        if (data.type === 'builder.editorLoaded') {
          this.postJson()
          if (this._theme || this._options) {
            this.postTheme()
            this.postOptions()
          } else {
            this.doneLoading()
          }
        }
        if (data.type === 'builder.themeSet') {
          this.doneLoading()
        }
        if (data.type === 'builder.optionsSet') {
          this.doneLoading()
        }
        if (data.type === 'builder.uploadAsset') {
          const { isImage, url } = data.data
          this.dispatchEvent(
            new CustomEvent('upload', {
              detail: {
                url,
                isImage
              }
            })
          )
        }
      }
    }

    private doneLoading() {
      if (this.loaded) {
        return
      }
      this.ready = true
      this.classList.add('loaded')
      this.dispatchEvent(new CustomEvent('load'))
      this.onReadyHandles.forEach(handler => handler())
    }

    injectStyles() {
      const stylesId = 'builder-editor-styles'
      if (document.getElementById(stylesId)) {
        return
      }
      const style = document.createElement('style')
      style.id = stylesId
      style.innerHTML = `
        builder-editor {
          display: block;
          position: relative
        }

        builder-editor iframe {
          position: absolute;

          top: 0px;
          left: 0px;
          width: 100%;
          height: 100%;

          min-height: 400px;
          min-width: 600px;
          border: none;
        }
      `
      document.head!.appendChild(style)
    }

    private messageFrame(name: string, value?: object) {
      if (this.iframeRef && this.iframeRef.contentWindow) {
        this.iframeRef.contentWindow.postMessage(
          {
            type: name,
            data: value
          },
          '*'
        )
      }
    }

    async getScreenshot() {
      await this.waitUntilReady()

      // TODO: promise.race with timeout
      return new Promise((resolve, reject) => {
        function listener(event: MessageEvent) {
          const { data } = event
          if (data.type === 'builder.editorScreenshot') {
            const dataUrl = data.data.screenshot
            if (dataUrl) {
              const blob = dataUrlToBlob(dataUrl)
              const file = new File([blob], 'studio-screenshot.jpg', {
                type: 'image/jpeg'
              })
              resolve(file)
            } else {
              reject('Screenshot failed')
            }
          }
        }
        window.addEventListener('message', listener)
        this.messageFrame('builder.getEditorScreenshot')
      })
    }

    private postJson() {
      if (this._json && this.iframeRef && this.iframeRef.contentWindow) {
        this.messageFrame('builder.updateEditorData', { data: this._json })
      }
    }

    private postOptions() {
      if (this._options && this.iframeRef && this.iframeRef.contentWindow) {
        this.messageFrame('builder.updateEditorOptions', { data: this.options })
      }
    }

    private postTheme() {
      if (this._theme && this.iframeRef && this.iframeRef.contentWindow) {
        this.messageFrame('builder.updateStudioTheme', { theme: this._theme })
      }
    }

    set data(data: any) {
      this._json = data
      this.postJson()
    }

    get data() {
      return this._json
    }

    set options(options: any) {
      this._options = options
      this.postOptions()
    }

    get options() {
      return {
        apiKey: this.apiKey,
        ...this._options
      }
    }

    set theme(theme: any) {
      this._theme = theme
      this.postTheme()
    }

    private _theme: any
    private _json: any
    private _options: any

    attributeChangedCallback() {
      // TODO: listen for attr change and respond accordingly - and same with properties
    }

    disconnectedCallback() {
      removeEventListener('message', this.onWindowMessage)
    }
  }
  customElements.define('builder-editor', BuilderEditor)
}
